import { APP_INITIALIZER, EnvironmentProviders, ErrorHandler, inject, isDevMode, NgModule } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { LenderCodeInterceptor } from './../shared/interceptors/lender-code-interceptor';
import { ErrorHandlerService } from '@shared/service/error-handler.service';
import { RetryInterceptor } from './../shared/interceptors/retry.interceptor';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  FooterModule,
  GlobalHeaderLibraryModule,
  GoogleTagManagerService,
  SharedUiLibraryModule,
  ThemeService,
  FeatureFlagService,
  AppConfigService as SharedAppConfigService,
  FeatureFlag,
  TOKEN_SERVICE_LOGGER,
  MalwareApiToken
} from '@cudirect/shared-ui-library';
import { LogLevel, DatadogModuleConfig } from '@cudirect/sawdust/types';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { buildDatadogConfig, DatadogService } from '@cudirect/sawdust/datadog';

import { PathGuardService } from '@shared/service/path-guard-service.service';
import { environment } from 'environments/environment';
import { AppConfigService } from 'src/app/config/config.service';
import { HeaderInterceptor } from '@shared/interceptors/header.interceptor';
import { UserSessionService } from 'src/shared/service/user-session.service';
import { SharedModule } from 'src/shared/shared.module';
import { WorkflowModule } from 'src/workflow/workflow.module';
import { AuthenticateGuard } from '../shared/service/authenticateguard.service';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routes';
import { PageNotFoundComponent } from './page/error/404/404.component';
import { GenericErrorComponent } from './page/generic_error/generic_error.component';
import { RedirectComponent } from './page/redirect/redirect.component';
import { InitializeLenderService } from './service/initialize-lender.service';
import { InitializeWorkflowWidgetService } from './service/initialize-workflow-widget.service';
import { AdminLoginComponent } from './page/admin-login/admin-login.component';
import { EaAuthenticateModule } from '@cudirect/ea-login';
import { AdminLogoutComponent } from './page/admin-logout/admin-logout.component';
import { InitializeAppConfigDataService } from './service/initialize-app.config.service';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { AdminConfig } from '@shared/interfaces/appInitConfig';
import { OrigenceLegalComponent } from '../origence-legal/origence-legal.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { PickListTypes } from '@workflow/ecp-workflow/dynamic-app/types/picklist';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ECP_WORKFLOW_ENVIRONMENT_TOKEN } from '@workflow/ecp-shared/service/environment/environment.service';
import { ProductsConfig } from '@workflow/class/config.type';
import { DynamicWorkflowModule } from '@workflow/dynamic-workflow.module';
import { ConfigService } from '@workflow/service/config/config.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { StatefulService } from './service/stateful.service';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { LenderService } from '@shared/service/lender/lender.service';
import { AlloyConfig } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy-config.const';
import { UserVerificationErrorComponent } from './page/user-verification-error/user-verification-error.component';
import { GlobalErrorHandler } from '@shared/service/global-error-handler.service';
import { provideSawDust } from '@shared/sawdust/sawdust.factory';
import { provideDatadog } from '@shared/datadog/datadog.module';
import { RuntimeConfigService } from './service/runtime-config.service';
import { LIB_VERSION } from './build-info';

const isDevLike = isDevMode() || environment.e2e;

// Important: Hydration/event replay stays in app.module.ts, but it now
// activates only when SSR markup (the ng-server-context attribute) is present.
// Moving it into app.module.server.ts wouldn’t help, because that module never
// ships to the browser; gating it this way keeps SSR happy while leaving the
// legacy SPA test runs untouched.
const hydrationProviders: EnvironmentProviders[] =
  typeof document !== 'undefined' && document.documentElement?.hasAttribute?.('ng-server-context')
    ? [provideClientHydration(withEventReplay())]
    : [];

// TODO: The 'SharedAppConfigService' is the correct one from SharedUI. It should replace both the local app config service
// and the environment data.

/** App Initializer Function
 ** TODO: Note that a promise failure in the APP_INIT chain can throw an 'ngOriginalError'.
 The recommended approach to fixing this is a try/catch block, but that does not result in
 better UI until we tackle this in #106643. Something to be aware of.
 * @param initializeLenderService a reference to the Lender Service, which provides lender configurations required by the app
 * @param initializeWWService a reference to the Workflow Widget Service, which initializes workflow routes and configurations
 * @returns promise that app is expecting to resolve to allow APP_INIT to continue
 */
const appInit = (
  initializeLenderService: InitializeLenderService,
  initializeWWService: InitializeWorkflowWidgetService,
  featureFlagService: FeatureFlagService,
  configService: ConfigService,
  sharedAppConfigService: SharedAppConfigService,
  initializeAppConfigService: InitializeAppConfigDataService,
  adminPortalFeaturesService: AdminPortalFeaturesService,
  l360Service: L360Service,
  statefulService: StatefulService,
  datadog: DatadogService,
  sawdust: SawDust,
  runtimeConfigService: RuntimeConfigService
) => {
  return async () => {
    const isBrowser = typeof window !== 'undefined';

    return await runtimeConfigService
      .loadConfig()
      .then(async (runtimeConfig) => {
        const dataDogConfig = buildDatadogConfig(runtimeConfig?.dataDog);
        if (dataDogConfig) {
          dataDogConfig.version = LIB_VERSION;
        }

        // Skip Datadog during E2E
        if (environment.datadogEnabled) {
          datadog.init(dataDogConfig);
        } else {
          sawdust.debug?.('Datadog disabled (E2E mode).');
        }
        sawdust.environment = dataDogConfig.env;

        // TODO: Need to determine is there an actual need to set the config data into a Shared App Config Service?
        // Currently being consumed by the token service for the startpasswordless, verifypasswordless, and refreshtoken methods.
        sharedAppConfigService.setSettingsData(runtimeConfig);
        // Get and set all Consolidated Configs for (Lender, Workflow, Feature, Picklist, Admin)
        await initializeAppConfigService.initAppConfig();

        // Filter feature config
        const featureFlagConfig = initializeAppConfigService.getConfigByKey<FeatureFlag[]>('features');
        // set feature flags config data
        if (featureFlagConfig) featureFlagService.featureFlagsConfigData = featureFlagConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Feature Flags.');

        // Filter lender config
        const lenderConfig = initializeAppConfigService.getConfigByKey<LenderConfiguration>('lender');
        // set lender config data
        if (lenderConfig) initializeLenderService.setLenderConfigData(lenderConfig);
        else initializeAppConfigService.handleInitFailure('Issue fetching Lender Config.');

        // Filter workflow config
        const workflowConfig = initializeAppConfigService.getConfigByKey<ProductsConfig>('workflow');
        // set workflow config data
        if (workflowConfig) {
          initializeWWService.setConfigData(
            // eslint-disable-next-line @typescript-eslint/dot-notation
            runtimeConfigService.getConfig?.externalWorkflowConfig?.configApi || environment['externalWorkflowConfig'].configApi,
            workflowConfig,
            runtimeConfigService.getDXAPIBaseUrl() // Passing the DX API base URL to the WW Service for the bundle API endpoints
          );
        } else initializeAppConfigService.handleInitFailure('Issue fetching Workflow Config.');

        // Filter admin config
        const adminConfig = initializeAppConfigService.getConfigByKey<AdminConfig>('admin');
        if (adminConfig) adminPortalFeaturesService.config = adminConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Admin Config.');

        // TODO: What is this doing?
        configService.addExternalConfig({ oao: { admin: adminConfig } });

        // Filter picklist config
        const pickListConfig = initializeAppConfigService.getConfigByKey<PickListTypes>('picklist');
        if (pickListConfig) l360Service.pickListConfigData = pickListConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Picklist Config.');

        if (isBrowser) {
          // sessionStorage.setItem('appOriginatedURL', location.href); // Getting app loading url for reporting
          // Unfortunately, this broke the appOriginatedURL, so we're using the StatefulValue service for 4.1
          // We should revisit this in trunk, and come up with a better solution
          // Currently, workflow is looking in sessionStore under this key cuASSD:ss
          // for anything saved in workflow configs using #{ss:somekey}
          statefulService.wrap('ss:appOriginatedURL').value = location.href; // Getting app loading url for reporting
        }
      })
      .catch((error) => {
        (globalThis as Record<string, unknown>).__APP_INIT_ERROR__ = error instanceof Error ? error.message : String(error ?? 'unknown error');
        initializeAppConfigService.handleInitFailure(error);
      })
      .finally(() => {
        (globalThis as Record<string, unknown>).__APP_READY__ = true;
      });
  };
};

// Root Module
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    RouterModule.forRoot([], { anchorScrolling: 'disabled', onSameUrlNavigation: 'reload', scrollPositionRestoration: 'top' }),
    BrowserAnimationsModule,
    SharedModule.forRoot(),
    SharedUiLibraryModule,
    FooterModule,
    GlobalHeaderLibraryModule,
    DynamicWorkflowModule,
    WorkflowModule,
    EaAuthenticateModule,
    NgxMaskDirective,
    NgxMaskPipe,
    UserVerificationErrorComponent,
    GenericErrorComponent,
    // Standalone components
    // AppComponent, ← include when NG19 migration is complete
    PageNotFoundComponent,
    AdminLoginComponent,
    RedirectComponent,
    AdminLogoutComponent,
    OrigenceLegalComponent
  ],
  providers: [
    ApiService,
    SharedAppConfigService,
    AuthenticateGuard,
    PathGuardService,
    GlobalService,
    UserService,
    AppConfigService,
    GoogleTagManagerService,
    ThemeService,
    LenderService,
    RuntimeConfigService,
    provideNgxMask(),
    {
      provide: ECP_WORKFLOW_ENVIRONMENT_TOKEN,
      useFactory: () => environment
    },
    {
      deps: [
        InitializeLenderService,
        InitializeWorkflowWidgetService,
        FeatureFlagService,
        ConfigService,
        SharedAppConfigService,
        InitializeAppConfigDataService,
        AdminPortalFeaturesService,
        L360Service,
        StatefulService,
        DatadogService,
        SawDust,
        RuntimeConfigService
      ],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: appInit
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LenderCodeInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (runtimeConfigService: RuntimeConfigService) => {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        return runtimeConfigService.getConfig?.recaptchaSiteKey || environment['recaptchaSiteKey'];
      },
      deps: [RuntimeConfigService]
    },
    UserSessionService,
    WorkflowService,
    CurrencyPipe,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: TOKEN_SERVICE_LOGGER,
      useFactory: () => {
        return async () => {
          const datadogService = inject(DatadogService);
          return datadogService;
        };
      }
    },
    {
      provide: MalwareApiToken,
      useFactory: (runtimeConfigService: RuntimeConfigService) => {
        return runtimeConfigService.getConfig?.malwareScan?.malwareApiToken || '';
      },
      deps: [RuntimeConfigService]
    },
    {
      provide: AlloyConfig,
      useFactory: (runtimeConfigService: RuntimeConfigService) => {
        return {
          initParams: {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            production: runtimeConfigService.getConfig?.isProd || environment['isProd'],
            showHeader: true,
            customStyle: {
              theme: {
                // AAA level status colors
                successColor: '#005E48',
                errorColor: '#B30813'
              }
            }
          },
          stepUpAnchor: 'id-verify-anchor',
          anchorIsDedicated: true
        };
      },
      deps: [RuntimeConfigService]
    },
    ...(isDevLike
      ? []
      : [
          {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
          }
        ]),
    ...provideDatadog(),
    ...provideSawDust('AppRoot', isDevLike ? 'dev' : 'prod', LogLevel[environment.logLevel]),
    provideHttpClient(withInterceptorsFromDi()),
    ...hydrationProviders
  ]
})
export class AppModule {}
